// Shared "this month vs last month" Sales/Purchase computation, used by every dashboard that
// shows the Sales/Purchase donuts (Top Level, Finance). Sources from the same generic,
// CC-scoped transactionLogSlice thunks (Client Invoice = Sales, Vendor Invoice = Purchase).
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchMonthlyClientInvoiceLog,
    fetchMonthlyVendorInvoiceLog,
    fetchPreviousMonthVendorInvoiceLog,
    fetchPreviousMonthClientInvoiceLog,
    selectMonthlyVendorInvoiceLog,
    selectMonthlyClientInvoiceLog,
    selectPreviousMonthVendorInvoiceLog,
    selectPreviousMonthClientInvoiceLog,
} from '../../slices/financialReportSlice/transactionLogSlice';

const calculateTransactionTotals = (data) => {
    if (!data?.Data || !Array.isArray(data.Data)) return 0;

    return data.Data.reduce((total, transaction) => {
        const debit = parseFloat(transaction.DebitValue || transaction.Debit || 0);
        const credit = parseFloat(transaction.CreditValue || transaction.Credit || 0);
        return total + debit + credit;
    }, 0);
};

export const useSalesPurchaseMetrics = (roleId, userId) => {
    const dispatch = useDispatch();

    const monthlyClientData = useSelector(selectMonthlyClientInvoiceLog);
    const monthlyVendorData = useSelector(selectMonthlyVendorInvoiceLog);
    const previousMonthClientData = useSelector(selectPreviousMonthClientInvoiceLog);
    const previousMonthVendorData = useSelector(selectPreviousMonthVendorInvoiceLog);

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // the transaction log SP restricts rows to this user's assigned cost centers.
        dispatch(fetchMonthlyVendorInvoiceLog({ roleId, userId }));
        dispatch(fetchMonthlyClientInvoiceLog({ roleId, userId }));
        dispatch(fetchPreviousMonthVendorInvoiceLog({ roleId, userId }));
        dispatch(fetchPreviousMonthClientInvoiceLog({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    const currentMonthSales = calculateTransactionTotals(monthlyClientData);
    const currentMonthPurchase = calculateTransactionTotals(monthlyVendorData);
    const previousMonthSales = calculateTransactionTotals(previousMonthClientData);
    const previousMonthPurchase = calculateTransactionTotals(previousMonthVendorData);

    const salesGrowth = previousMonthSales > 0
        ? ((currentMonthSales - previousMonthSales) / previousMonthSales * 100).toFixed(1)
        : 0;

    const purchaseGrowth = previousMonthPurchase > 0
        ? ((currentMonthPurchase - previousMonthPurchase) / previousMonthPurchase * 100).toFixed(1)
        : 0;

    return {
        currentMonthSales,
        currentMonthPurchase,
        previousMonthSales,
        previousMonthPurchase,
        salesGrowth: parseFloat(salesGrowth),
        purchaseGrowth: parseFloat(purchaseGrowth),
    };
};
